import React, {useState, FC, useEffect} from "react";
import {makeStyles, Theme} from "@material-ui/core/styles";
import {
    Grid,
    Button,
    Dialog,
    DialogActions,
    DialogTitle,
    Typography,
    Box, TextField, Select, InputAdornment, MenuItem, IconButton
} from "@material-ui/core";
import moment from "moment";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import API from "../../helpers/apiHelpers";
import {useSelector} from "react-redux";
import {RootState} from "../../redux/store";
import {DatePicker, MuiPickersUtilsProvider} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import {Autocomplete} from "@material-ui/lab";

const useStyles = makeStyles((theme: Theme) => ({
    dialog: {
        padding: "48px",
        "& .MuiFormControlLabel-label": {
            minWidth: "100px"
        },
        [theme.breakpoints.down("xs")]: {
            padding: "28px 20px"
        }
    },
    dialogTitle: {
        fontSize: "24px",
        fontWeight: "bold",
        padding: "0"
    },
    section: {
        marginTop: "34px",
        marginBottom: "20px",
        fontSize: "18px",
        fontWeight: 700
    },
    actions: {
        display: "flex",
        justifyContent: "flex-start",
        padding: 0,
        marginTop: "23px"
    },
    button: {
        padding: "12px 27px",
    },
    label: {
        fontSize: 18,
        marginBottom: 16
    },
    dateSelect: {
        color: "#000000",
        "& .MuiInputBase-input": {
            color: "#000000",
        }
    }
}));

interface FilterParams {
    parentLastName?: string,
    parentFirstName?: string,
    animalId?: number,
    endDate?: string,
    startDate?: string,
    isFinished?: boolean,
    offset?: number
}

// eslint-disable-next-line react/prop-types
const FilterAppointments = ({isOpen, setOpenDialog, handleChange = null, offset, setOffset}) => {
    const classes = useStyles();
    const [profiles, setProfiles] = useState([]);
    const [profile, setProfile] = useState(null);
    const [animals, setAnimals] = useState(null);
    const [animal, setAnimal] = useState(null);
    const [dateTime, setDateTime] = useState(null);
    const [changes, setChanges] = useState([]);
    const [queryProfile, setQueryProfile] = React.useState("");
    const user = useSelector((state: RootState) => state?.user);
    const datePickerInputProps = {
        style: {
            // height: 48,
            color: "#000000",
            fontSize: 18,
            width: 210
        },
        endAdornment: (
            <InputAdornment position="start">
                <CalendarTodayIcon fontSize="small"/>
            </InputAdornment>
        ),
        startAdornment: (
            <InputAdornment position="start">
                <ExpandMoreIcon fontSize="small"/>
            </InputAdornment>
        )
    };

    const handleClose = () => {
        setProfile(null);
        setAnimal(null);
        setAnimals([]);
        setDateTime(null);
        setOpenDialog(false);
    };

    const filter = () => {
        setOffset(0);
        setChanges([]);
        let params: FilterParams = {};
        if (profile) {
            params.parentLastName = profile.lastName;
            params.parentFirstName = profile.firstName;
        }
        if (animal) params.animalId = animal.id;
        if (dateTime) params.startDate = moment(dateTime).format("YYYY-MM-DD");
        if (dateTime) params.endDate = moment(dateTime).format("YYYY-MM-DD");
        params.isFinished = true;
        params.offset = 0;
        handleChange(params);
        setOpenDialog(false);
    };

    const getProfiles = async (vetName) => {
        try {
            const profileList = await API.getProfiles({type: "OWNER", name: vetName});
            setProfiles(profileList);
        } catch (err) {
            console.log(err);
        }
    };
    useEffect(() => {
        if (queryProfile.length) {
            const timeOutId = setTimeout(() => getProfiles(queryProfile), 500);
            return () => clearTimeout(timeOutId);
        }
    }, [queryProfile]);

    const getAnimals = async () => {
        try {
            const animalList = await API.getAnimal(profile.userId);
            setAnimals(animalList);
        } catch (err) {
            console.log(err);
        }
    };

    useEffect(() => {
        if (profile) {
            getAnimals();
        }
    }, [profiles, profile]);

    useEffect(() => {
        setOpenDialog(isOpen);
    }, [isOpen]);

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            classes={{paper: classes.dialog}}
            scroll="body"
            disableEnforceFocus
        >
            <DialogTitle color="primary" classes={{root: classes.dialogTitle}}>
                <Typography variant="h3">Search Past Appointments</Typography>
            </DialogTitle>
            <Box marginTop="20px" marginBottom="5px">
                <Typography color="textSecondary" className={classes.label}>
                    Add information to one or more fields to search past appointments.
                </Typography>
            </Box>
            <Grid container spacing={2}>
                <Grid item sm={6} xs={12}>
                    <Typography variant="h4" className={classes.label}>Pet Parent</Typography>
                    <Autocomplete
                        id="userProfile"
                        value={profile}
                        onChange={(event, value) => {
                            setAnimal(null);
                            setAnimals([]);
                            setProfile(value);
                        }}
                        options={profiles}
                        style={{
                            width: 210,
                            color: "#000000",
                            height: 48
                        }}
                        getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                        renderInput={(params) =>
                            <TextField
                                {...params}
                                classes={{
                                    root: classes.dateSelect
                                }}
                                variant="outlined"
                                onChange={(event) => setQueryProfile(event.target.value)}
                            />}
                    />
                </Grid>
                <Grid item sm={6} xs={12}>
                    {!!animals?.length &&
                    <>
                        <Typography variant="h4" className={classes.label}>Animal</Typography>
                        <Autocomplete
                            id="animalSelect"
                            value={animal}
                            onChange={(event, value) => setAnimal(value)}
                            options={animals}
                            style={{
                                width: 210,
                                height: 48
                            }}
                            classes={{
                                root: classes.dateSelect
                            }}
                            getOptionLabel={(option) => `${option.name} (Breed: ${option.breed})`}
                            renderInput={(params) =>
                                <TextField
                                    classes={{
                                        root: classes.dateSelect
                                    }}
                                    {...params}
                                    variant="outlined"
                                />}
                        />
                    </>
                    }
                </Grid>
                <Grid item sm={6} xs={12}>
                    <Typography variant="h4" className={classes.label}>Appointment Date</Typography>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <DatePicker
                            autoOk
                            views={["date"]}
                            format="MMM dd, yyyy"
                            disableFuture={true}
                            value={dateTime}
                            inputVariant="outlined"
                            clearable
                            onChange={(val) => {
                                // @ts-ignore
                                setDateTime(val);
                                setChanges([...changes, "dateTime"]);
                            }}
                            InputProps={datePickerInputProps}
                        />
                    </MuiPickersUtilsProvider>
                </Grid>
            </Grid>
            <DialogActions classes={{root: classes.actions}}>
                <Button type="button" color="secondary" variant="contained" onClick={filter} className={classes.button}>
                    Search
                </Button>
                <Button type="button" onClick={handleClose} variant="contained" className={classes.button}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FilterAppointments;
